import { useCallback, useState } from 'react';
import { MONEY_PURPOSES, formatPence, type AdminTransferView } from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { DataTable } from '@london-life/ui/admin/data-table';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../../lib/api';
import { useAdmin } from '../../lib/admin-context';
import { when } from '../../lib/format';
import { useRequest } from '../../lib/use-request';

const person = (party: AdminTransferView['from']) =>
  party.username ? `${party.firstName} (@${party.username})` : party.firstName;
const purpose = (transfer: AdminTransferView) =>
  `${MONEY_PURPOSES[transfer.purpose].icon} ${MONEY_PURPOSES[transfer.purpose].label}${transfer.forRequest ? ' · paying a request' : ''}`;
const status = (transfer: AdminTransferView) =>
  transfer.status === 'held' ? (
    <Badge>Waiting for a check</Badge>
  ) : transfer.status === 'sent' ? (
    <Badge variant="secondary">{transfer.decidedAt ? 'Sent on after a check' : 'Sent'}</Badge>
  ) : (
    <Badge variant="outline">Returned</Badge>
  );

// Transfers between players: large ones wait here (the money sits in the system's "held" account) until an
// admin sends each on to the receiver or returns it, fee and all, to the sender. Every decision needs a reason
// and is audited; the people in the chat see the card change.
export function TransfersPage() {
  const { can } = useAdmin();
  const list = useRequest(useCallback(() => api.admin.economy.transfers(), []));
  const [deciding, setDeciding] = useState<{ transfer: AdminTransferView; decision: 'send' | 'return' } | null>(null);
  const [reason, setReason] = useState('');
  const columns = (actions: boolean) => [
    { key: 'when', header: 'When', render: (transfer: AdminTransferView) => when(transfer.createdAt) },
    {
      key: 'who',
      header: 'From → to',
      render: (transfer: AdminTransferView) => `${person(transfer.from)} → ${person(transfer.to)}`,
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (transfer: AdminTransferView) => (
        <span>
          <strong>{formatPence(transfer.amountPence)}</strong>
          {transfer.feePence > 0 && <span className="admin-muted"> + {formatPence(transfer.feePence)} fee</span>}
        </span>
      ),
    },
    {
      key: 'for',
      header: 'For',
      render: (transfer: AdminTransferView) => (
        <span>
          {purpose(transfer)}
          {transfer.note && <span className="admin-muted"> · “{transfer.note}”</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (transfer: AdminTransferView) => (
        <span>
          {status(transfer)}
          {transfer.decisionReason && <span className="admin-muted"> {transfer.decisionReason}</span>}
        </span>
      ),
    },
    ...(actions
      ? [
          {
            key: 'actions',
            header: '',
            className: 'text-right',
            render: (transfer: AdminTransferView) =>
              can('economy.write') && (
                <div className="admin-page-actions justify-end">
                  <Button size="sm" onClick={() => (setReason(''), setDeciding({ transfer, decision: 'send' }))}>
                    Send on
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => (setReason(''), setDeciding({ transfer, decision: 'return' }))}
                  >
                    Return
                  </Button>
                </div>
              ),
          },
        ]
      : []),
  ];
  return (
    <section>
      <PageHeader
        title="Transfers"
        description="Money players send each other. Transfers above the review line (set on the Money page) wait here until an admin sends them on or returns them to the sender."
      />
      {list.error && <ErrorNotice error={list.error} onRetry={() => void list.reload()} />}
      <div className="admin-panel">
        <h2>Waiting for a check</h2>
        <DataTable
          rows={list.data?.held ?? null}
          loading={list.busy}
          rowKey={(transfer) => transfer.id}
          empty="Nothing is waiting."
          columns={columns(true)}
        />
      </div>
      <div className="admin-panel">
        <h2>Latest transfers</h2>
        <DataTable
          rows={list.data?.recent ?? null}
          loading={list.busy}
          rowKey={(transfer) => transfer.id}
          empty="No player has sent money yet."
          columns={columns(false)}
        />
      </div>
      {deciding && (
        <FormDialog
          open
          title={deciding.decision === 'send' ? 'Send this transfer on?' : 'Return this transfer?'}
          description={`${formatPence(deciding.transfer.amountPence)} from ${person(deciding.transfer.from)} to ${person(
            deciding.transfer.to,
          )}. ${
            deciding.decision === 'send'
              ? 'The receiver gets it straight away.'
              : `All ${formatPence(deciding.transfer.amountPence + deciding.transfer.feePence)} goes back to the sender, fee included.`
          }`}
          submitLabel={deciding.decision === 'send' ? 'Send on' : 'Return'}
          canSubmit={reason.trim().length >= 5}
          onClose={() => setDeciding(null)}
          onSubmit={async () => {
            await api.admin.economy.decideTransfer(deciding.transfer.id, {
              decision: deciding.decision,
              reason: reason.trim(),
            });
            setDeciding(null);
            await list.reload();
          }}
        >
          <div className="admin-field">
            <Label htmlFor="transfer-reason">Reason</Label>
            <Input
              id="transfer-reason"
              value={reason}
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
            />
            <p className="admin-muted">Recorded in the audit log. At least 5 characters.</p>
          </div>
        </FormDialog>
      )}
    </section>
  );
}
